import React from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {PlacesService, UserService} from '../api';
import {MyWeatherSyncPlace} from '../api/types';
import {
  attachSavedPlaceIds,
  clearPendingStationIds,
  forgetDeletedPlaces,
  getDebugEmail,
  getFavoriteLocations,
  queueDeletedPlaces,
  selectDeletedServerIds,
  selectMyWeatherSyncEnabled,
  selectPendingStationIds,
  selectToken,
  selectUnsyncedFavoritePlaces,
} from '../store';
import {CommonRootState} from '../store/types';
import {MyWeather} from '../utils';
import useApiClient, {ApiError} from './useApiClient';
import {useUserEdit} from './userContext';

// The most one sync request may carry (backend SYNC_PLACES_MAX and
// SYNC_STATIONS_MAX): more is sent in further requests.
const PLACES_PER_SYNC = 200;
const STATIONS_PER_SYNC = 5000;

type Batch = {
  deletes: string[];
  places: MyWeatherSyncPlace[];
  stationIds: number[];
};

const isSyncPlace = (
  place: MyWeatherSyncPlace | null,
): place is MyWeatherSyncPlace => place !== null;

/** What the account does not hold yet, as one request can carry it. */
const nextBatch = (state: CommonRootState): Batch => ({
  deletes: selectDeletedServerIds(state),
  // A place the server would refuse stays on the device (`toSyncPlace`).
  places: selectUnsyncedFavoritePlaces(state)
    .map(MyWeather.toSyncPlace)
    .filter(isSyncPlace)
    .slice(0, PLACES_PER_SYNC),
  stationIds: selectPendingStationIds(state).slice(0, STATIONS_PER_SYNC),
});

const isEmpty = (batch: Batch) =>
  batch.deletes.length === 0 &&
  batch.places.length === 0 &&
  batch.stationIds.length === 0;

const isNotFound = (error: unknown) =>
  error instanceof ApiError && error.statusCode === 404;

// Silent: a background upload is not the user's business. Never on behalf
// of an account a staff member is logged in as, even one Login As starts
// while the request waits for a token.
const OPTIONS = {silent: true, skipLoginAs: true};

/**
 * Brings the account in step with this device (backend#266). It deletes
 * there the places removed here, then uploads what this device holds and
 * the account does not: favourite places with no server id yet, and the
 * station favourites captured at sign-in. It covers users who were signed
 * in before updating too, since they never sign in again.
 *
 * - The server only adds, so a repeat is harmless; each place and station is
 *   added after the account's own order.
 * - One run at a time, draining batch by batch, and it stops on a failure
 *   or an answer that settles nothing: what is left goes at the next launch,
 *   or the next change, never in a loop.
 * - Never under Login As, and an answer that arrives after a sign-out or an
 *   account switch is dropped.
 * - Afterwards /me is read again: it carries the merged order and the
 *   account's station favourites, uploaded ones included. A change made
 *   while it is out goes in another run straight after, unless that /me
 *   fails or the session changed (then it waits for the next change).
 *
 * Wire this once near the top of the tree (the UserSyncProviders).
 */
const useMyWeatherSync = () => {
  const {fetch} = useApiClient();
  const editUserDetails = useUserEdit();
  const dispatch = useDispatch();
  const store = useStore<CommonRootState>();
  const enabled = useSelector(selectMyWeatherSyncEnabled);
  const token = useSelector(selectToken);
  const debugEmail = useSelector(getDebugEmail);
  const pending = useSelector<CommonRootState, Batch>(nextBatch, isEqual);
  const running = React.useRef(false);
  // Bumped when a run ends with a change it was not started for: see the
  // end of `run`.
  const [rerun, setRerun] = React.useState(0);

  React.useEffect(() => {
    if (!enabled || !token || debugEmail || running.current) {
      return;
    }
    if (isEmpty(pending)) {
      return;
    }
    running.current = true;
    const owner = store.getState().user.email;
    const sameSession = () => {
      const {user} = store.getState();
      return user.token != null && !user.debugEmail && user.email === owner;
    };

    let again = false;
    const run = async () => {
      let batch = nextBatch(store.getState());
      let synced = false;
      while (!isEmpty(batch)) {
        // Removals first: an upload after them may save a removed place
        // again, under a new id, which is what the user then wants. Gone
        // already counts as deleted; any other failure stops the run.
        const {deletes} = batch;
        if (deletes.length > 0) {
          await Promise.all(
            deletes.map((id) =>
              fetch(PlacesService.deletePlace(id), OPTIONS).catch((error) => {
                if (!isNotFound(error)) {
                  throw error;
                }
              }),
            ),
          );
          if (!sameSession()) {
            return;
          }
          dispatch(forgetDeletedPlaces(deletes));
          synced = true;
        }
        if (batch.places.length === 0 && batch.stationIds.length === 0) {
          batch = nextBatch(store.getState());
          continue;
        }
        const answer = await fetch(
          PlacesService.syncMyWeather({
            places: batch.places,
            station_ids: batch.stationIds,
            order: [
              ...batch.stationIds.map(MyWeather.stationToken),
              ...batch.places.map((place) =>
                MyWeather.placeToken(place.client_id),
              ),
            ],
          }),
          OPTIONS,
        );
        if (!sameSession()) {
          return;
        }
        // Removed here while the request was out: the account took it
        // anyway. Queued, so the next pass deletes it before any /me, and
        // a failed delete is retried rather than lost.
        const wanted = new Set(
          getFavoriteLocations(store.getState()).map((l) => l.hash),
        );
        dispatch(
          queueDeletedPlaces(
            answer.places
              .filter((place) => !wanted.has(place.client_id))
              .map((place) => place.id),
          ),
        );
        dispatch(
          attachSavedPlaceIds(
            answer.places
              .filter((place) => wanted.has(place.client_id))
              .map((place) => ({hash: place.client_id, serverId: place.id})),
          ),
        );
        dispatch(clearPendingStationIds(batch.stationIds));
        const next = nextBatch(store.getState());
        if (isEqual(next, batch)) {
          break; // the answer settled nothing: asking again would loop
        }
        synced = true;
        batch = next;
      }
      if (synced) {
        const data = await fetch(UserService.getUserDetail(), OPTIONS);
        if (!sameSession()) {
          return;
        }
        editUserDetails(data);
      }
      // A change made while the /me was out reached the effect while this
      // run was going, and was turned away: nothing else would run for it
      // before the next change or launch, so another run follows. Not for
      // what this one stopped on (nothing, or a batch the server settled
      // nothing of), or a refusal would loop.
      again = !isEqual(nextBatch(store.getState()), batch);
    };

    run()
      .catch((error) => {
        // A failed request waits for the next launch or change. Anything
        // else is a bug, and must not vanish in a background task.
        if (!(error instanceof ApiError)) {
          throw error;
        }
      })
      .finally(() => {
        running.current = false;
        if (again) {
          setRerun((n) => n + 1);
        }
      });
  }, [
    enabled,
    token,
    debugEmail,
    pending,
    rerun,
    fetch,
    dispatch,
    store,
    editUserDetails,
  ]);
};

export default useMyWeatherSync;
